import { useState, useEffect, useRef } from 'react'
import { LogOut, User as UserIcon } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import type { User } from './AppShell'

export interface UserMenuProps {
  user: User
  onLogout?: () => void
}

export function UserMenu({ user, onLogout }: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleProfileClick = () => {
    setIsOpen(false)
    navigate('/profile')
  }

  const handleLogoutClick = () => {
    setIsOpen(false)
    onLogout?.()
  }

  return (
    <div className="relative" ref={menuRef}>
      <UserAvatarButton
        user={user}
        onClick={() => setIsOpen(!isOpen)}
      />
      {isOpen && (
        <DropdownMenu
          user={user}
          onProfileClick={handleProfileClick}
          onLogoutClick={handleLogoutClick}
        />
      )}
    </div>
  )
}

interface UserAvatarButtonProps {
  user: User
  onClick: () => void
}

function UserAvatarButton({ user, onClick }: UserAvatarButtonProps) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 p-0.5 hover:bg-slate-800 rounded-full transition-colors"
      aria-label="User menu"
    >
      <UserAvatar user={user} />
    </button>
  )
}

interface UserAvatarProps {
  user: User
}

function UserAvatar({ user }: UserAvatarProps) {
  if (user.avatarUrl) {
    return (
      <img
        src={user.avatarUrl}
        alt={user.name}
        className="object-cover w-8 h-8 rounded-full ring-2 ring-slate-700"
      />
    )
  }

  return (
    <div className="flex items-center justify-center w-8 h-8 text-sm font-semibold rounded-full bg-amber-500 text-slate-900">
      {getUserInitials(user.name)}
    </div>
  )
}

interface DropdownMenuProps {
  user: User
  onProfileClick: () => void
  onLogoutClick: () => void
}

function DropdownMenu({ user, onProfileClick, onLogoutClick }: DropdownMenuProps) {
  return (
    <div className="absolute z-50 mt-2 overflow-hidden border rounded-lg shadow-xl right-1 w-30 top-full bg-slate-800 border-slate-700">
      <ProfileOption user={user} onClick={onProfileClick} />
      <div className="py-2">
        <LogoutOption onClick={onLogoutClick} />
      </div>
    </div>
  )
}

interface ProfileOptionProps {
  user: User
  onClick: () => void
}

function ProfileOption({ user, onClick }: ProfileOptionProps) {
  return (
    <button
      onClick={onClick}
      className="flex items-center w-full gap-3 px-4 py-3 transition-colors border-b text-slate-100 border-slate-700 hover:bg-slate-700 hover:text-amber-400"
    >
      <UserIcon className="w-4 h-4" />
      <p className="font-medium">{user.name}</p>
    </button>
  )
}

interface LogoutOptionProps {
  onClick: () => void
}

function LogoutOption({ onClick }: LogoutOptionProps) {
  return (
    <button
      onClick={onClick}
      className="flex items-center w-full gap-3 px-4 py-2 transition-colors text-slate-300 hover:bg-slate-700 hover:text-rose-500"
    >
      <LogOut className="w-4 h-4" />
      <span>أخرج  </span>
    </button>
  )
}

function getUserInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}
